<template>
  <rmp-base-modal
    v-model="isOpen"
    extra-classes="BorderedHeaderModal Tab-variant"
    prevent-outside-click
    @base-modal:closed="refreshData"
  >
    <template #modal-header>
      <h2 class="u-text-center">
        Add Media
      </h2>
      <section class="TabMenu">
        <ul class="u-flex">
          <li
            :class="{'Selected': selectedTab === 'image'}"
            class="TabMenu-item"
            @click="selectedTab = 'image'"
          >
            Image
          </li>
          <li
            v-if="layoutType === 'media-middle'"
            :class="{'Selected': selectedTab === 'video'}"
            class="TabMenu-item"
            @click="selectedTab = 'video'"
          >
            Video
          </li>
        </ul>
      </section>
    </template>
    <template #modal-body>
      <div
        v-if="selectedTab === 'image'"
        class="ImageTab"
      >
        <div class="MediaSection">
          <template v-if="showImageUpload">
            <label
              class="MediaUpload-wrapper"
              :for="`${question.vuexId}-MediaUpload`"
            >
              <span class="Icon-s Icon-cloud-upload-alt" />
              Image Upload
              <span class="Hint">Max 10mb 1080px / 1040px</span>
            </label>
            <input
              :id="`${question.vuexId}-MediaUpload`"
              class="MediaUploader"
              type="file"
              name="logoFilename"
              accept="image/*"
              @change="handleMediaSelection($event)"
            >
          </template>
          <div
            v-else
            class="Media-Container"
          >
            <button
              class="Icon-s Icon-trash RemoveButton"
              @click="handleRemoveLayoutMedia"
            />
            <img
              :src="localUnsavedQuestion.layout.image"
              alt=""
            >
          </div>
        </div>
      </div>
      <div
        v-if="selectedTab === 'video'"
        class="VideoTab"
      >
        <rmp-autocomplete
          v-model="localUnsavedQuestion.layout.videoSource"
          label="Select Provider"
          :items="videoSources"
          option-key="label"
        />

        <div
          v-if="localUnsavedQuestion.layout.videoSource"
          class="Notice Notice--info u-flex"
        >
          <template v-if="localUnsavedQuestion.layout.videoSource.description">
            <span :class="providerIcon" />
            <p>{{ localUnsavedQuestion.layout.videoSource.description }}</p>
          </template>
        </div>

        <rmp-input
          v-model="localUnsavedQuestion.layout.videoId"
          label="Enter Video ID"
        />
      </div>
    </template>
    <template #modal-footer>
      <div class="u-floatLeft">
        <button
          class="Button Button--transparent"
          @click="isOpen = false"
        >
          Cancel
        </button>
      </div>
      <div class="u-floatRight">
        <button
          :disabled="isAddMediaButtonDisabled"
          :class="[{'is-loading': ui.submitLoading }, 'Button Button--green']"
          @click="handleAddMedia"
        >
          {{ buttonLabel }}
        </button>
      </div>
    </template>
  </rmp-base-modal>
</template>
<script>
import { mapMutations, mapState } from 'vuex';
import RmpBaseModal from '@rmpenterprise/base-modal';
import RmpAutocomplete from '@rmpenterprise/form-autocomplete-select';
import RmpInput from '@rmpenterprise/form-input';
import ModalActionsMixin from './mixins/modal-actions-mixin';

export default {
  components: { RmpBaseModal, RmpAutocomplete, RmpInput },
  mixins: [ModalActionsMixin],
  props: {
    question: {
      type: Object,
      default() {
        return {};
      },
    },
    mediaType: {
      type: String,
      default: 'image',
    },
    layoutType: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      ui: {
        submitLoading: false,
      },
      localUnsavedQuestion: {
        layout: {
          image: null,
          video: null,
          videoSource: null,
        },
      },
      selectedTab: this.mediaType,
    };
  },
  computed: {
    ...mapState('forms', {
      videoSources: (state) => state.layoutVideoSources,
    }),
    providerIcon() {
      if (this.localUnsavedQuestion.videoSource === 'youtube') {
        return 'Icon-s Icon-youtube';
      }
      if (this.localUnsavedQuestion.videoSource === 'vimeo') {
        return 'Icon-s Icon-vimeo';
      }

      return '';
    },

    isAddMediaButtonDisabled() {
      if (this.selectedTab === 'image') {
        return !this.localUnsavedQuestion.layout.image;
      }
      return !this.localUnsavedQuestion.layout.videoId || !this.localUnsavedQuestion.layout.videoSource;
    },

    buttonLabel() {
      return this.selectedTab === 'image' ? 'Add Image' : 'Add Video';
    },
    showImageUpload() {
      return !this.localUnsavedQuestion.layout.image;
    },
  },
  methods: {
    ...mapMutations('forms', ['setScreenLayoutType', 'updateSelectedQuestion', 'resetSelectedQuestion']),
    refreshData() {
      this.handleNoMediaSelected();
      this.selectedTab = 'image';
      this.localUnsavedQuestion = {
        layout: {
          image: null,
        },
      };
    },
    handleNoMediaSelected() {
      const {
        image, videoSource, videoId, type,
      } = this.question.layout;

      if (type === 'media-middle') {
        if (!image && !videoSource && !videoId) {
          // Reset the layout to text-only since no media has been added
          this.setScreenLayoutType('text-only');
          this.resetSelectedQuestion();
        }
      } else if (!image) {
        // Reset the layout to text-only since no media has been added
        this.setScreenLayoutType('text-only');
        this.resetSelectedQuestion();
      }
    },
    handleAddVideo() {
      const question = {
        ...this.question,
        layout: {
          ...this.question.layout, videoUrl: null, videoId: this.localUnsavedQuestion.layout.videoId, videoSource: this.localUnsavedQuestion.layout.videoSource.value,
        },
      };
      this.updateSelectedQuestion(question);

      // Call this function to ensure question builder panel does not open
      this.resetSelectedQuestion();
      this.isOpen = false;
    },

    handleAddMedia() {
      if (this.selectedTab === 'image') {
        this.updateSelectedQuestion(this.localUnsavedQuestion);
        this.resetSelectedQuestion();
      } else {
        this.handleAddVideo();
      }
      this.isOpen = false;
    },

    handleRemoveLayoutMedia() {
      this.localUnsavedQuestion.layout.image = null;
    },

    handleMediaSelection({ currentTarget: input }) {
      // Take the uploaded image and convert to a base64 string in order to show a preview in the browser
      // Add this to the event in order for it to be added to the form.
      if (input.files && input.files[0]) {
        const reader = new FileReader();

        reader.onload = (e) => {
          // Save the image locally until confirmation eg add image or add video
          this.localUnsavedQuestion = {
            ...this.question,
            layout: { ...this.question.layout, image: e.target.result },
          };
        };

        reader.readAsDataURL(input.files[0]); // convert to base64 string
      }
    },
  },
};
</script>
<style lang="less" scoped>
.BorderedHeaderModal {
  h3, p {
    color: #013C83;
  }
  .TabMenu {
    margin-top: 30px;

    ul {
      justify-content: center;
      margin-bottom: 0;

      .TabMenu-item {
        padding: 0 20px;
        cursor: pointer;

        &.Selected {
          border-bottom: 2px solid #046FF0;
        }
      }
    }
  }

  .MediaSection {
    position: relative;
    height: 200px;
    width: 80%;
    border-radius: 6px;
    background: #E0E4EA;
    border: 1px solid #E0E4EA;
    margin: 0 auto;

    &:hover .RemoveButton {
      display: block;
      position: absolute;
      right: 0;
      top: -12px;
      cursor: pointer;
      border-radius: 50%;
      color: white;
      background-color: #F10000;
      font-size: .9rem;
      height: 24px;
      width: 24px;
      margin-left: 5px;
    }

    .RemoveButton {
      display: none;
    }

    .MediaMiddleSelection-container {
      padding-top: 20px;
      .AddMedia-label {
        color: #046FF0;
      }
    }

    .AddImage-container {
      position: relative;

      .MediaUploader {
        display: none;
      }
    }

    .MediaUpload-wrapper {
      top: 25%;
      position: relative;
      display: block;
      text-align: center;
      padding: 10px;

      .Icon-s {
        display: block;
        margin-bottom: 5px;
        font-size: 1.4rem;
      }

      .Hint {
        display: block;
      }
    }

    .MediaUploader {
      visibility: hidden;
    }

    .Media-Container {
      height: 100%;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }
}
</style>
